/**
 * E07-F04-T02 — the Review "Add company" (single) + "Bulk add" (paste, one per
 * line) dialogs.
 *
 * 1:1 port of the prototype `../EER app/prototype/assets/research.proto.js`
 * (openAddCompany/confirmAddCompany L1029-1045, openBulkAdd/confirmBulkAdd
 * L1072-1092): a specialist can hand-add a company they know is a donor but the
 * discovery pass missed, or paste a batch (one company / site per line). Both add
 * rows flagged needs-human server-side (unverified) so the recruiter runs Verify
 * to enrich them before publishing.
 *
 * These compose the shared `Dialog` primitive (navy header, scrim, focus-trap,
 * Esc / return-focus) and call the existing review api (`useAddCompany` /
 * `useBulkAddCompanies`) — no new endpoints. The parent (ResearchReviewView)
 * owns the open flags; a successful add closes the dialog and toasts.
 */
import { useState, type JSX } from 'react';
import { REVIEW_GRADES, type ReviewGrade } from '@eer/shared';
import { Dialog, DialogFooter } from '../ui/Dialog.js';
import { useToast } from '../ui/ToastContext.js';
import { useAddCompany, useBulkAddCompanies } from './researchReviewApi.js';

// ---------------------------------------------------------------------------
// Add company (single)
// ---------------------------------------------------------------------------

export interface AddCompanyDialogProps {
  readonly requestId: number;
  readonly open: boolean;
  readonly onClose: () => void;
}

/** The single-company add dialog (name + optional site + optional grade). */
export function AddCompanyDialog({ requestId, open, onClose }: AddCompanyDialogProps): JSX.Element {
  const toast = useToast();
  const add = useAddCompany(requestId);
  const [name, setName] = useState('');
  const [site, setSite] = useState('');
  const [grade, setGrade] = useState<ReviewGrade | ''>('');

  function reset(): void {
    setName('');
    setSite('');
    setGrade('');
  }

  function close(): void {
    reset();
    onClose();
  }

  async function confirm(): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Enter a company name');
      return;
    }
    try {
      await add.mutateAsync({
        name: trimmed,
        site: site.trim() || undefined,
        grade: grade || null,
      });
      toast.success(`${trimmed} added · flagged needs-human · hit Verify to enrich`);
      close();
    } catch {
      toast.error('Failed to add the company');
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Add company"
      testId="add-company-dialog"
      maxWidth="md"
      footer={
        <DialogFooter
          onCancel={close}
          onConfirm={() => void confirm()}
          confirmLabel={add.isPending ? 'Adding…' : 'Add company'}
          confirmDisabled={add.isPending}
          variant="accent"
          testId="add-company-dialog-footer"
        />
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-strong">Company name</span>
          <input
            type="text"
            data-testid="add-company-name"
            className="rounded-md border border-border p-2 text-sm outline-none focus:border-accent"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Robotics"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-strong">Website (optional)</span>
          <input
            type="text"
            data-testid="add-company-site"
            className="rounded-md border border-border p-2 text-sm outline-none focus:border-accent"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            placeholder="acme.com"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-strong">Grade (optional)</span>
          <select
            data-testid="add-company-grade"
            className="rounded-md border border-border bg-white p-2 text-sm outline-none focus:border-accent"
            value={grade}
            onChange={(e) => setGrade(e.target.value as ReviewGrade | '')}
          >
            <option value="">— unset —</option>
            {REVIEW_GRADES.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </label>
        <p className="text-xs text-text-muted">
          Added rows are flagged <b>needs-human</b> — run Verify to enrich + confirm before
          publishing.
        </p>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Bulk add (paste, one per line)
// ---------------------------------------------------------------------------

export interface BulkAddDialogProps {
  readonly requestId: number;
  readonly open: boolean;
  readonly onClose: () => void;
}

/** The bulk-add dialog (paste a list; the service splits/trims/dedups lines). */
export function BulkAddDialog({ requestId, open, onClose }: BulkAddDialogProps): JSX.Element {
  const toast = useToast();
  const bulk = useBulkAddCompanies(requestId);
  const [text, setText] = useState('');

  function close(): void {
    setText('');
    onClose();
  }

  async function confirm(): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error('Paste at least one company');
      return;
    }
    try {
      const res = await bulk.mutateAsync(trimmed);
      toast.success(`${res.added} companies added · flagged needs-human · hit Verify to enrich`);
      close();
    } catch {
      toast.error('Failed to bulk-add companies');
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Bulk add companies"
      testId="bulk-add-dialog"
      maxWidth="lg"
      footer={
        <DialogFooter
          onCancel={close}
          onConfirm={() => void confirm()}
          confirmLabel={bulk.isPending ? 'Adding…' : 'Add all'}
          confirmDisabled={bulk.isPending}
          variant="accent"
          testId="bulk-add-dialog-footer"
        />
      }
    >
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-text-strong" htmlFor="bulk-add-text">
          One company or site per line
        </label>
        <textarea
          id="bulk-add-text"
          data-testid="bulk-add-text"
          className="min-h-[160px] rounded-md border border-border p-2 font-mono text-xs outline-none focus:border-accent"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={'Acme Robotics\nnorthstar.io\nContoso Manufacturing'}
        />
        <p className="text-xs text-text-muted">
          A bare domain (e.g. <code>acme.com</code>) is treated as a site; everything else as a
          name. All rows land flagged <b>needs-human</b>.
        </p>
      </div>
    </Dialog>
  );
}
